import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from 'recoil';
import { ToggleDocumentDownloadPack } from '../../../../Component/Api/DocumentUpload/DocumentUploadHelper';
import { BarrierService } from "../../../../Component/Api/EmploymentEquity/Plan/Barriers/BarrierService";
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../../../Component/Common/Components/DataTable/DataTable';
import { ColumnSortOption } from "../../../../Component/Model/Dto/ColumnSortOption";
import { DocumentsForDownloadParameters } from '../../../../Component/Model/Dto/Document';
import { EEBarrier } from "../../../../Component/Model/Dto/EmploymentEquity/Plan/EEBarriers";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { defaultObject } from '../../../../Helpers/typescriptHelper';
import { ScorecardAtom } from '../../../../State/Scorecard.Atom';
import config from "../../../../config";
import { SupportingDocuments } from '../../../Common/SupportingDocuments/SupportingDocuments';
import { EEBarriersTableMapper } from "./EEBarriersTableColumn";
import { EEPlanBarrierModal } from "./EEPlanBarrierModal";
import { EEPlanBarrierPeopleModal } from "./EEPlanBarrierPeopleModal";
import { EEPlanBarriersColumns } from "./EEPlanBarriersColumns";
import { ConsolidationAtom } from "../../../../State/Consolidation.Atom";
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { BarriersSection } from "../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";

export const EEPlanBarriers = () => {
    const [previewPopup, setPreviewPopup] = React.useState<boolean>(false);
    const [barrierDtoInData, setBarrierDtoInData] = React.useState<EEBarrier.SearchResult[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedBarrierId, setSelectedBarrierId] = React.useState<string>("");
    const [showDocumentsPopUp, setDocumentsPopUp] = React.useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = React.useState<string>("");
    const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const scorecard = useRecoilValue(ScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);


    const docLink = 'EmploymentEquityBarriers';
    const ScorecardId = scorecard.scorecardId;
    const confirmDeleteMessage = "This action cannot be reversed. Are you sure you want to delete the barrier and all the associated notes and supporting documents?"


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const handleClosePopup = () => {
        setPreviewPopup(false);
        setSelectedBarrierId("");
        fetchData();
    }

    const handleDocumentCancel = () => {
        fetchData();
        setDocumentsPopUp(false);
    }

    const handleEdit = (id: string) => {
        setSelectedBarrierId(id);
        setPreviewPopup(true);
    }

    const handleAdd = () => {
        setPreviewPopup(true);
    }

    const handleConfirmDelete = (id: string) => {
        setSelectedBarrierId(id);
        setShowConfirmationDialog(true);
    }

    const barriersLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: BarriersSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: ScorecardId,
            requestUrl: window.location.href
        });
    } 

    const handleDelete = async () => {
        try {
            await BarrierService.DeleteBarrier(selectedBarrierId);
            barriersLog(UserActivityActionType.Delete, "Delete a barrier")
            toast.success("Barrier deleted succesfully");
            handleCloseConfirmation();
            fetchData();

        } catch (error) {
            console.log(error)
        }
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedBarrierId("");
    }

    const handleAttachedSD = (id: string) => {
        setSelectedDocumentId(id);
        setDocumentsPopUp(true);
    }

    const handleToggleDocumentPack = async (id: string) => {

        let added = barrierDtoInData.find((barrier: any) => barrier.id === id)?.documentsSelectedForDownload;
        setSelectedDocumentId(id);
        if (added !== undefined) {

            const toggleDocumentsParamters: DocumentsForDownloadParameters = {
                entityId: id,
                scorecardId: ScorecardId,
                link: docLink
            };

            await ToggleDocumentDownloadPack(toggleDocumentsParamters, added);
            fetchData();
        }

    }

    const barrierSearchRequest: EEBarrier.SearchRequest = {
        pageNumber: pageNumber as number,
        pageSize: entriesPerPage.defaultValue as number,
        scorecardId: scorecard.scorecardId,
        columnSortOptions: columnSortOptions
    };

    const bulkActions =
        [{
            label: "Add Barrier",
            action: handleAdd
        }]


    const fetchData = async () => {
        setIsLoading(true);
        try {
            let searchResult = await BarrierService.SearchBarriers(barrierSearchRequest);
            if (searchResult.data) {
                setBarrierDtoInData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }

    };

    useEffect(() => {
        fetchData();
        barriersLog(UserActivityActionType.View, "View list of barriers")
    }, [entriesPerPage, pageNumber, columnSortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const barrierTableData = EEBarriersTableMapper(barrierDtoInData, handleEdit, handleConfirmDelete, handleAttachedSD, handleToggleDocumentPack, isConsolidatedScorecard);

    const searchValue = (searchValue: string) => { }

    return (
        <div style={{ marginTop: "5px" }}>
            <DataTable
                key={"BarriersTable"}
                table={barrierTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch
                searchValueChanged={searchValue}
                bulkActions={bulkActions}
                manualSorting={true}
                columnSortingChanged={columnSortingChanged}
            ></DataTable>

            {previewPopup && (config.enable_employment_equity_salary === 'true' ? (
                <EEPlanBarrierModal
                    id={selectedBarrierId}
                    handleCancel={handleClosePopup}
                    refreshTable={fetchData}
                    scorecardId={ScorecardId}
                    link={docLink}
                    setBarrierId={setSelectedBarrierId}
                    previewPopup={previewPopup} />
            ) : (
                <EEPlanBarrierPeopleModal
                    id={selectedBarrierId !== "" ? selectedBarrierId : undefined}
                    handleCancel={handleClosePopup}
                    refreshTable={fetchData}
                    scorecardId={ScorecardId}
                    link={docLink}
                    setBarrierId={setSelectedBarrierId}
                    previewPopup={previewPopup} />
            ))}

            {showDocumentsPopUp && <SupportingDocuments display={showDocumentsPopUp} handleCancel={handleDocumentCancel} link={docLink} id={selectedDocumentId} 
                scorecardId={ScorecardId} isEEModule={true} section={BarriersSection}/>}

            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={handleDelete}
                handleCancel={handleCloseConfirmation}
                heading={"Confirm Delete"}
                message={confirmDeleteMessage}
            />
        </div>
    )
}
